import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../api/api';

import Header from '../../components/Header';
import Footer from '../../components/Footer';
import MobileNavigation from '../../components/MobileNavigation';
import { ratingToPercent } from '../../utils/rating';

import { useUser } from '../../contexts/user';
import log from '../../utils/logger';
import { useScrollRestoration } from '../../hooks/useScrollRestoration';
import { DRIVE_SPEED_KMH } from '../../config/appConfig';

const Category = () => {
  const { t, i18n } = useTranslation();
  const { categoryId } = useParams();
  const navigate = useNavigate();

  const [stores, setStores] = useState([]);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  useScrollRestoration(!loading);

  const { user } = useUser();

  useEffect(() => {
    const fetchStores = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/buyer/store/storeCategory/${categoryId}`);
        setStores(res.data?.response?.data?.merchantData || []);
        setCategory(res.data?.response?.data?.category || null);
      } catch (err) {
        log.error('Error fetching stores by category:', err);
      } finally {
        setLoading(false);
      }
    };

    if (categoryId) {
      fetchStores();
    }
  }, [categoryId]);

  if (loading) {
    return (
      <>
        <section className="flex flex-col min-h-dvh mb-20 lg:mb-0">
          <Header categoryName={i18n.language === 'fr' ? category?.frName : category?.enName} />
          <main className="flex-grow min-h-0">
            <section className="pb-20 lg:pb-40 pt-8 lg:pt-20 px-2 lg:px-5">
              <div className="hidden lg:block h-7 w-48 bg-gray-200 rounded animate-pulse mb-4 max-w-[1300px] mx-auto"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 max-w-[1300px] mx-auto w-full">
                {Array.from({ length: 6 }).map((_, idx) => (
                  <div key={idx} className="rounded-lg overflow-hidden border border-[#e9e9e9]">
                    <div className="h-[150px] sm:h-[180px] md:h-[200px] w-full bg-gray-200 animate-pulse"></div>
                    <div className="bg-white py-[14px] px-3 md:px-4 flex items-center gap-3">
                      <div className="w-[62px] h-[62px] bg-gray-200 rounded-lg animate-pulse"></div>
                      <div className="flex-1 flex flex-col gap-2">
                        <div className="h-4 w-3/4 bg-gray-200 rounded animate-pulse"></div>
                        <div className="h-3 w-1/2 bg-gray-100 rounded animate-pulse"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </main>
        </section>
        <MobileNavigation />
        <Footer />
      </>
    );
  }

  const getDistanceKm = (lat1, lon1, lat2, lon2) => {
    if (
      typeof lat1 !== 'number' || typeof lon1 !== 'number' ||
      typeof lat2 !== 'number' || typeof lon2 !== 'number'
    ) {
      return null;
    }
    const R = 6371; // radius of Earth in km
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return +(R * c).toFixed(2);
  };

  const getTravelTimeByCar = (distanceKm) => {
    if (typeof distanceKm !== 'number' || distanceKm <= 0) {
      return null;
    }
    const timeHours = distanceKm / DRIVE_SPEED_KMH;
    const timeMinutes = Math.round(timeHours * 60);
    return timeMinutes;
  };

  return (
    <>
      <section className="flex flex-col min-h-dvh mb-20 lg:mb-0">
        <Header
          categoryName={i18n.language === 'fr' ? category?.frName : category?.enName}
        />

        <main className="flex-grow min-h-0">
          <section className="pb-20 lg:pb-40 pt-8 lg:pt-20 px-2 lg:px-5">
            <h2 className="hidden lg:block mb-4 text-xl font-medium text-[#000000DE] max-w-[1300px] mx-auto w-full capitalize">
              {i18n.language === 'fr' ? category?.frName : category?.enName}
            </h2>
            {stores.length === 0 ? (
              <div className="text-center text-gray-600 py-20">
                {t('No merchants found in this category.')}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 max-w-[1300px] mx-auto w-full">
                {[...stores].sort((a, b) => {
                  const distA = user?.locations?.[0]
                    ? getDistanceKm(
                      user.locations[0].latitude,
                      user.locations[0].longitude,
                      a.address?.latitude,
                      a.address?.longitude,
                    )
                    : Infinity;
                  const distB = user?.locations?.[0]
                    ? getDistanceKm(
                      user.locations[0].latitude,
                      user.locations[0].longitude,
                      b.address?.latitude,
                      b.address?.longitude,
                    )
                    : Infinity;
                  return distA - distB;
                }).map((store, index) => {
                  const ratingPercent = ratingToPercent(store.rating?.averageRating);
                  return (
                    <div
                      key={index}
                      className="nearby-merchants-card rounded-lg overflow-hidden cursor-pointer"
                      onClick={() => navigate(`/merchant-details/${store._id}`)}
                    >
                      <div
                        className="h-[150px] sm:h-[180px] md:h-[200px] w-full relative bg-cover bg-center"
                        style={{ backgroundImage: `url(${store.coverImage || ''})` }}
                      >
                        {ratingPercent && (
                          <div className="text-xs leading-4 flex items-center justify-center gap-1 py-[6px] px-3 bg-black text-white rounded-[100px] absolute right-2 bottom-2">
                            {ratingPercent}
                          </div>
                        )}
                        {store.sponsored && (
                          <span className="bg-[#000000B3] py-1 px-2 text-xs text-white leading-4 rounded-tr text-center absolute left-0 bottom-0">
                            {t('Sponsored')}
                          </span>
                        )}
                      </div>

                      {/* Details */}
                      <div className="bg-white py-4 px-3 md:px-4 flex items-center gap-3 flex-1">
                        <div className="w-[62px] h-[62px] rounded-lg overflow-hidden">
                          <img
                            src={store.logoImage || ''}
                            alt={store.storeName}
                            loading="lazy"
                            className="w-full h-full object-cover"
                          />
                        </div>

                        <div className="flex flex-col gap-1.5">
                          <h3 className="leading-[1.2] capitalize truncate font-semibold mb-[1px] text-[15.2px]">
                            {store.storeName}
                          </h3>
                          <ul className="text-xs flex items-center font-medium text-[#6F6F6F]">
                            {(() => {
                              const dist = user?.locations?.[0]
                                ? getDistanceKm(
                                  user.locations[0].latitude,
                                  user.locations[0].longitude,
                                  store.address?.latitude,
                                  store.address?.longitude,
                                )
                                : null;

                              const travelTime = dist ? getTravelTimeByCar(dist) : null;

                              return (
                                <>
                                  {travelTime && <li>{travelTime} {t('mins')}</li>}
                                  {dist && (
                                    <li className="flex items-center">
                                      <div className="w-1 h-1 bg-[#6F6F6F] rounded-full mx-2"></div>
                                      {dist} {t('km')}
                                    </li>
                                  )}
                                </>
                              );
                            })()}
                          </ul>
                          {(store.categories?.enNames && store.categories?.enNames) && (
                            <p className="font-medium text-xs leading-4 truncate text-[#8F8F8F]">
                              {i18n.language === 'en' ? store.categories.enNames.join(', ') : store.categories.frNames.join(', ')}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </main>

      </section>
      <MobileNavigation />
      <Footer />
    </>
  );
};

export default Category;
